/**
 * Share Service
 *
 * Handles sharing functionality using Capacitor Share API
 * Falls back to download on platforms that don't support native sharing
 */

import { Share } from '@capacitor/share';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Capacitor } from '@capacitor/core';
import { svgToBase64Png } from '../utils/image-export';
import type { NeedGuess } from '../types';

/**
 * Clean up old share temp files from cache
 * Should be called on app startup
 */
export async function cleanupShareCache(): Promise<void> {
  // Only run on native platforms
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  try {
    const result = await Filesystem.readdir({
      path: '',
      directory: Directory.Cache,
    });

    const now = Date.now();
    const maxAge = 24 * 60 * 60 * 1000; // 24 hours

    const deletionPromises = result.files
      .filter((file) => file.name.startsWith('empathease-') && file.name.endsWith('.png'))
      .map(async (file) => {
        const fileTime = file.mtime ? new Date(file.mtime).getTime() : 0;
        if (fileTime && now - fileTime > maxAge) {
          try {
            await Filesystem.deleteFile({
              path: file.name,
              directory: Directory.Cache,
            });
          } catch (err) {
            // Log individual errors but don't stop the whole cleanup
            const errorMessage = err instanceof Error ? err.message : 'Unknown error';
            console.warn(`Failed to delete cached file '${file.name}':`, errorMessage);
          }
        }
      });

    await Promise.all(deletionPromises);
  } catch (error) {
    // Cache cleanup is non-critical, just log
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    console.log('Cache cleanup skipped:', errorMessage);
  }
}

/**
 * Write base64 image to a temporary file and return the URI
 * @param {string} base64Data - Base64 image data (with or without data URL prefix)
 * @param {string} filename - Filename for the temp file
 * @returns {Promise<string>} File URI suitable for sharing
 */
export async function writeImageToTempFile(base64Data: string, filename: string): Promise<string> {
  // Remove data URL prefix if present
  const base64Content = base64Data.replace(/^data:image\/\w+;base64,/, '');

  // Write to cache directory
  const result = await Filesystem.writeFile({
    path: filename,
    data: base64Content,
    directory: Directory.Cache,
  });

  return result.uri;
}

/**
 * Check if native sharing is available
 * @returns {Promise<boolean>} True if share is available
 */
export async function canShare(): Promise<boolean> {
  try {
    const result = await Share.canShare();
    return result.value;
  } catch (error) {
    console.log('Share API not available:', error);
    return false;
  }
}

interface TransformationData {
  originalText: string;
  transformedText: string;
  pitfallCount: number;
}

/**
 * Share a transformation result as an image
 * @param {Object} transformationData - The transformation data
 * @param {string} transformationData.originalText - Original jackal text
 * @param {string} transformationData.transformedText - Transformed giraffe text
 * @param {number} transformationData.pitfallCount - Number of pitfalls addressed
 * @returns {Promise<void>}
 * @throws {Error} If sharing fails
 */
export async function shareTransformation(transformationData: TransformationData): Promise<void> {
  const {
    originalText,
    transformedText: _transformedText,
    pitfallCount: _pitfallCount,
  } = transformationData;

  try {
    // Get the SVG element from the DOM
    const svgElement = document.getElementById('shareable-transformation-card');

    if (!svgElement) {
      throw new Error('Shareable card not found. Please try again.');
    }

    // Convert SVG to base64 PNG (mobile-friendly portrait: 1080x1350)
    const base64Image = (await svgToBase64Png(
      svgElement as unknown as SVGElement,
      1080,
      1350,
      2,
    )) as string;

    // Prepare share text (URL provided separately via url parameter)
    const shareText =
      `I transformed my message with EmpathEase! 🦒\n\n` +
      `Before: "${originalText.substring(0, 100)}${originalText.length > 100 ? '...' : ''}"`;

    // Try native sharing first
    const shareAvailable = await canShare();

    if (shareAvailable) {
      // Write image to temp file for mobile sharing
      const fileUri = await writeImageToTempFile(
        base64Image,
        `empathease-transformation-${Date.now()}.png`,
      );

      await Share.share({
        title: 'My NVC Transformation',
        text: shareText,
        url: 'https://empathease.app',
        dialogTitle: 'Share Your Transformation',
        files: [fileUri],
      });
    } else {
      // Fallback: Download the image
      downloadImage(base64Image, 'empathease-transformation.png');
      throw new Error('Share not available. Image downloaded instead.');
    }
  } catch (error) {
    console.error('Share error:', error);

    // If it's a user cancellation, don't throw
    const errorMessage = error instanceof Error ? error.message : '';
    if (errorMessage && errorMessage.includes('cancel')) {
      return;
    }

    throw error;
  }
}

/**
 * Download an image (fallback for platforms without native sharing)
 * @param {string} base64Data - Base64 image data
 * @param {string} filename - Filename for the download
 */
function downloadImage(base64Data: string, filename: string): void {
  const link = document.createElement('a');
  link.href = base64Data;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

interface NeedsData {
  acceptedNeeds: NeedGuess[];
  totalNeeds: number;
}

/**
 * Share needs visualization as an image
 * @param {Object} needsData - The needs visualization data
 * @param {Array} needsData.acceptedNeeds - Array of accepted needs
 * @param {number} needsData.totalNeeds - Total number of needs identified
 * @returns {Promise<void>}
 * @throws {Error} If sharing fails
 */
export async function shareNeedsVisualization(needsData: NeedsData): Promise<void> {
  const { acceptedNeeds, totalNeeds: _totalNeeds } = needsData;

  try {
    // Get the SVG element from the DOM
    const svgElement = document.getElementById('shareable-needs-card');

    if (!svgElement) {
      throw new Error('Shareable card not found. Please try again.');
    }

    // Get actual SVG dimensions
    const width = parseInt(svgElement.getAttribute('width') || '1080') || 1080;
    const height = parseInt(svgElement.getAttribute('height') || '1350') || 1350;

    // Convert SVG to base64 PNG using actual dimensions
    const base64Image = (await svgToBase64Png(
      svgElement as unknown as SVGElement,
      width,
      height,
      2,
    )) as string;

    // Create needs list for share text
    const needsList = acceptedNeeds
      .slice(0, 3)
      .map((need) => `• ${need.universal_need}`)
      .join('\n');

    // Prepare share text (URL provided separately via url parameter)
    const shareText =
      `I discovered my needs with EmpathEase! 🎈\n\n` +
      `${needsList}${acceptedNeeds.length > 3 ? `\n...and ${acceptedNeeds.length - 3} more` : ''}`;

    // Try native sharing first
    const shareAvailable = await canShare();

    if (shareAvailable) {
      // Write image to temp file for mobile sharing
      const fileUri = await writeImageToTempFile(
        base64Image,
        `empathease-my-needs-${Date.now()}.png`,
      );

      await Share.share({
        title: 'My Needs Visualization',
        text: shareText,
        url: 'https://empathease.app',
        dialogTitle: 'Share Your Needs',
        files: [fileUri],
      });
    } else {
      // Fallback: Download the image
      downloadImage(base64Image, 'empathease-my-needs.png');
      throw new Error('Share not available. Image downloaded instead.');
    }
  } catch (error) {
    console.error('Share error:', error);

    // If it's a user cancellation, don't throw
    const errorMessage = error instanceof Error ? error.message : '';
    if (errorMessage && errorMessage.includes('cancel')) {
      return;
    }

    throw error;
  }
}

/**
 * Share transformation to specific platform (if needed for future enhancements)
 * @param {string} _platform - Platform name (e.g., 'whatsapp', 'telegram')
 * @param {Object} transformationData - The transformation data
 * @returns {Promise<void>}
 */
export async function shareToSpecificPlatform(
  _platform: string,
  transformationData: TransformationData,
): Promise<void> {
  // For now, just use the general share function
  // In the future, could add platform-specific deep links or formatting
  return await shareTransformation(transformationData);
}
